<template>
  <div>

    <future-alert :end="end" />

    <v-row class="mb-5">
      <v-col class="text-end">

        <v-btn-toggle
          v-model="formState.mode"
          rounded="0"
          group
          divided
          variant="outlined"
          mandatory
          density="compact"
        >
          <v-btn v-for="modeOption in modeOptions" :value="modeOption">
            {{ modeOption.label }}
          </v-btn>
        </v-btn-toggle>

      </v-col>
    </v-row>

    <v-row>

      <v-col cols="12" md="6" lg="4">
        <average-rate-breakdown
          :date-summaries="dateSummaries"
          :mode="formState.mode"
          :loading="loading"
          class="h-100"
        />
      </v-col>

      <v-col cols="12" md="6" lg="4">
        <revenue-breakdown
          :date-summaries="dateSummaries"
          :mode="formState.mode"
          :loading="loading"
          class="h-100"
        />
      </v-col>

      <v-col cols="12" md="6" lg="4">
        <transaction-breakdown
          :date-summaries="dateSummaries"
          :mode="formState.mode"
          :loading="loading"
          class="h-100"
        />
      </v-col>

    </v-row>

    <v-row>
      <v-col>
        <gross-revenue-by-channel
          :date-summaries="dateSummaries"
          :mode="formState.mode"
          :loading="loading"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <vertical-rollup
          :date-summaries="dateSummaries"
          :mode="formState.mode"
          :loading="loading"
        />
      </v-col>
    </v-row>

  </div>
</template>

<script setup lang="ts">

import { reactive } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTitle } from '@vueuse/core'
import AverageRateBreakdown from './reports/AverageRateBreakdown.vue'
import FutureAlert from './reports/FutureAlert.vue'
import GrossRevenueByChannel from './reports/GrossRevenueByChannel.vue'
import RevenueBreakdown from './reports/RevenueBreakdown.vue'
import TransactionBreakdown from './reports/TransactionBreakdown.vue'
import VerticalRollup from './reports/VerticalRollup.vue'
import ChannelRegistry from '../../../channels/ChannelRegistry'
import VerticalRegistry from '../../../verticals/VerticalRegistry'
import type { ReportingDateSummaries } from '../../../services/api-clients/ReportingApi'
import type { BreakdownMode } from './types'
import { Temporal } from '@js-temporal/polyfill'

/**
 * Props
 */

interface Props {
  loading: boolean
  dateSummaries: ReportingDateSummaries
  end: null | Temporal.PlainDate
}

withDefaults(defineProps<Props>(), {
  loading: false,
})

/**
 *
 */

const { t } = useI18n()
const title = useTitle()
title.value = t('titles.reporting_breakdowns')

/**
 * Form
 */

const channelLabels: {[key: string]: string} = {}
const channelColors: {[key: string]: string} = {}

ChannelRegistry.get().forEach((channel) => {
  channelLabels[channel.name] = channel.isParcsChannel ? t('titles.parcs') : channel.titleShort
  channelColors[channel.name] = channel.color
})

const modeOptions: BreakdownMode[] = [
  {
    name: 'channel',
    label: t('titles.channel'),
    labelsEnum: channelLabels,
    colorsEnum: channelColors,
    dataKey: 'by_channel',
  },
  {
    name: 'vertical',
    label: t('titles.vertical'),
    labelsEnum: VerticalRegistry.getOptionsMap(),
    colorsEnum: VerticalRegistry.getColorMap(),
    dataKey: 'by_vertical',
  },
]

const formState = reactive({
  mode: modeOptions[0],
})

</script>
